import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { FeedbackApiBase } from './feedback-api-base';
import {
  AddReasoningRequest,
  BulkStatusUpdateResult,
  BulkUpdateFeedbackStatusRequest,
  CreateFeedbackRequest,
  DataEnvelope,
  Feedback,
  FeedbackStatus,
  FeedbackStub,
  FeedbackUserOption,
  GetFeedbackStubRequest,
  ListFeedbackQuery,
  MessageResultEnvelope,
  Paginated,
  StatusChangedBy,
  UpdateFeedbackStatusRequest,
} from '../models';

/**
 * Mirrors App\Http\Controllers\FeedbackController.
 * All endpoints live under `/api/v1`.
 */
@Injectable({ providedIn: 'root' })
export class FeedbackService extends FeedbackApiBase {
  /** POST /v1/feedbackstub */
  getFeedbackStub(request: GetFeedbackStubRequest): Observable<FeedbackStub> {
    return this.http
      .post<DataEnvelope<FeedbackStub>>(this.url('feedbackstub'), request)
      .pipe(map((response) => response.data));
  }

  /** POST /v1/feedback */
  createFeedback(request: CreateFeedbackRequest): Observable<Feedback> {
    return this.http
      .post<DataEnvelope<Feedback>>(this.url('feedback'), request)
      .pipe(map((response) => response.data));
  }

  /** GET /v1/feedback */
  listFeedback(query: ListFeedbackQuery): Observable<Paginated<Feedback>> {
    return this.http
      .get<DataEnvelope<Paginated<Feedback>>>(this.url('feedback'), {
        params: this.buildParams({ ...query }),
      })
      .pipe(map((response) => response.data));
  }

  /** GET /v1/feedback/urls — distinct base URLs with feedback in a round. */
  listFeedbackUrls(feedbackRoundId: string): Observable<string[]> {
    return this.http
      .get<DataEnvelope<string[]>>(this.url('feedback/urls'), {
        params: this.buildParams({ feedbackRoundId }),
      })
      .pipe(map((response) => response.data));
  }

  /**
   * GET /v1/feedback/users — the distinct authors with feedback in a round, as
   * `{ value, label }` options for the "filter by user" dropdown. Includes an
   * "AI Reviewer" option (value `'ai'`) when the round has AI-generated feedback.
   */
  listFeedbackUsers(feedbackRoundId: string): Observable<FeedbackUserOption[]> {
    return this.http
      .get<DataEnvelope<FeedbackUserOption[]>>(this.url('feedback/users'), {
        params: this.buildParams({ feedbackRoundId }),
      })
      .pipe(map((response) => response.data));
  }

  /** GET /v1/feedback/{id} */
  getFeedback(id: string): Observable<Feedback> {
    return this.http
      .get<DataEnvelope<Feedback>>(this.url(`feedback/${id}`))
      .pipe(map((response) => response.data));
  }

  /** DELETE /v1/feedback/{id} — resolves to the result message. */
  deleteFeedback(id: string): Observable<string> {
    return this.http
      .delete<MessageResultEnvelope>(this.url(`feedback/${id}`))
      .pipe(map((response) => response.data.message));
  }

  /**
   * PUT /v1/feedback/{id}/status/{newStatus} — resolves to the result message.
   * @param changedBy Who is making the change, recorded in the item's status history.
   */
  updateFeedbackStatus(id: string, newStatus: FeedbackStatus, changedBy?: StatusChangedBy): Observable<string> {
    const request: UpdateFeedbackStatusRequest = changedBy ? { changedBy } : {};
    return this.http
      .put<MessageResultEnvelope>(this.url(`feedback/${id}/status/${newStatus}`), request)
      .pipe(map((response) => response.data.message));
  }

  /**
   * PUT /v1/feedbackrounds/{feedbackRoundId}/feedback/status — sets one status on
   * several of the round's items (at most 200).
   * @param changedBy Who is making the change, recorded in each item's status history.
   */
  bulkUpdateFeedbackStatus(
    feedbackRoundId: string,
    feedbackIds: string[],
    newStatus: FeedbackStatus,
    changedBy?: StatusChangedBy,
  ): Observable<BulkStatusUpdateResult> {
    const request: BulkUpdateFeedbackStatusRequest = { feedbackIds, status: newStatus, ...(changedBy ? { changedBy } : {}) };
    return this.http
      .put<DataEnvelope<BulkStatusUpdateResult>>(this.url(`feedbackrounds/${feedbackRoundId}/feedback/status`), request)
      .pipe(map((response) => response.data));
  }

  /** PUT /v1/feedback/{id}/reasoning — resolves to the result message. */
  addReasoning(id: string, reasoning: string): Observable<string> {
    const request: AddReasoningRequest = { reasoning };
    return this.http
      .put<MessageResultEnvelope>(this.url(`feedback/${id}/reasoning`), request)
      .pipe(map((response) => response.data.message));
  }
}
